import { useState, useEffect } from "react";
import { useAppContext } from "../../context/AppContext";
import { PWAInstallTrans } from "../../utils/translations";

const STORAGE_KEY = "pwa-install-dismissed";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (Number.isNaN(at)) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function dismiss() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {}
}

export default function PWAInstallBanner() {
  const { language } = useAppContext();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);

  const t = PWAInstallTrans[language] || PWAInstallTrans.english;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || wasDismissedRecently()) return;

    // Chrome/Edge: beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    // Already installed (e.g. from previous visit)
    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    // iOS: no beforeinstallprompt; show hint after a short delay so we don't flash on desktop
    if (isIOS() && !isStandalone()) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
        window.removeEventListener("appinstalled", handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    dismiss();
    setVisible(false);
  };

  if (!visible || installed) return null;

  const isIOSDevice = isIOS() && !deferredPrompt;

  return (
    <div
      className="rounded-2xl mt-5shadow-lg border border-amber-200/80 bg-gradient-to-br from-amber-50 to-amber-100/60 overflow-hidden"
      role="region"
      aria-label={t.message}
    >
      <div className="flex items-start sm:items-center gap-4 p-4 sm:p-5">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-200/70 flex items-center justify-center text-xl" aria-hidden>
          📱
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {isIOSDevice ? t.iosHint : t.message}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
          {!isIOSDevice && (
            <button
              type="button"
              onClick={handleInstall}
              className="px-4 py-2.5 rounded-xl bg-brown text-white font-semibold text-sm shadow-md hover:bg-amber-800 active:scale-[0.98] transition-all"
            >
              {t.install}
            </button>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            className="text-xs text-amber-700/80 hover:text-amber-900 py-1 sm:py-0 transition-colors"
            aria-label={t.dismiss}
          >
            {t.dismiss}
          </button>
        </div>
      </div>
    </div>
  );
}
